import { create } from 'zustand';
import { DataFile, Column } from '../types';
import { dataApi } from '../services/api';

interface DataState {
  // 数据列表
  dataFiles: DataFile[];
  
  // 当前选中的数据
  selectedData: DataFile | null;
  
  // 数据结构
  schema: Column[] | null;
  
  // 预览数据
  previewData: Record<string, unknown>[] | null;
  
  // 加载状态
  isLoading: boolean;
  isUploading: boolean;
  
  // 错误信息
  error: string | null;
  
  // 操作
  setDataFiles: (files: DataFile[]) => void;
  setSelectedData: (data: DataFile | null) => void;
  fetchDataList: () => Promise<void>;
  fetchDataDetail: (id: string) => Promise<void>;
  fetchDataSchema: (id: string) => Promise<void>;
  fetchPreviewData: (id: string, limit?: number) => Promise<void>;
  uploadData: (file: File, name?: string) => Promise<void>;
  deleteData: (id: string) => Promise<void>;
  clearSelectedData: () => void;
}

export const useDataStore = create<DataState>()((set) => ({
  dataFiles: [],
  selectedData: null,
  schema: null,
  previewData: null,
  isLoading: false,
  isUploading: false,
  error: null,

  setDataFiles: (files) => set({ dataFiles: files }),
  
  setSelectedData: (data) => set({ selectedData: data }),

  fetchDataList: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await dataApi.listData();
      if (response.code === 0) {
        set({ dataFiles: response.data.data });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchDataDetail: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await dataApi.getData(id);
      if (response.code === 0) {
        set({ selectedData: response.data });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchDataSchema: async (id) => {
    try {
      const response = await dataApi.getDataSchema(id);
      if (response.code === 0) {
        // Map API schema to Column type with index
        const schemaWithIndex: Column[] = response.data.schema.map((col: { name: string; type: string; nullable: boolean; semanticType?: string }, idx: number) => ({
          name: col.name,
          type: col.type as import('../types').DataType,
          nullable: col.nullable,
          index: idx,
          semanticType: col.semanticType,
        }));
        set({ schema: schemaWithIndex });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    }
  },

  fetchPreviewData: async (id, limit = 100) => {
    try {
      const response = await dataApi.previewData(id, limit);
      if (response.code === 0) {
        set({ previewData: response.data.data });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    }
  },

  uploadData: async (file, name) => {
    set({ isUploading: true, error: null });
    try {
      const response = await dataApi.uploadData(file, name);
      if (response.code === 0) {
        set((state) => ({
          dataFiles: [response.data, ...state.dataFiles],
        }));
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isUploading: false });
    }
  },

  deleteData: async (id) => {
    try {
      await dataApi.deleteData(id);
      set((state) => ({
        dataFiles: state.dataFiles.filter((f) => f.id !== id),
        selectedData: state.selectedData?.id === id ? null : state.selectedData,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    }
  },

  clearSelectedData: () =>
    set({
      selectedData: null,
      schema: null,
      previewData: null,
    }),
}));
